/**
 * User Management Page
 * Untuk owner/admin mengelola user (buat, edit, reset password, deactivate)
 */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser,
  User, 
  UserCreate, 
  UserUpdate 
} from '../../services/userService';
import { getBranches } from '../../services/branchService';
import { Branch } from '../../types/branch';
import ResetPasswordForm from './ResetPasswordForm';

const UserManagementPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form state
  const [formData, setFormData] = useState<UserCreate>({
    username: '',
    password: '',
    full_name: '',
    role: 'kasir',
    branch_id: undefined,
  });

  // Filters
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterBranch, setFilterBranch] = useState<number | undefined>(undefined);

  useEffect(() => {
    loadData();
  }, [filterRole, filterBranch]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, branchesData] = await Promise.all([
        getUsers({ 
          role: filterRole || undefined,
          branch_id: filterBranch,
        }),
        getBranches(),
      ]);
      setUsers(usersData);
      setBranches(branchesData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createUser(formData);
      alert('User berhasil dibuat!');
      setShowCreateForm(false);
      setFormData({
        username: '',
        password: '',
        full_name: '',
        role: 'kasir',
        branch_id: undefined,
      });
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Gagal membuat user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      const updateData: UserUpdate = {
        full_name: formData.full_name,
        role: formData.role,
        branch_id: formData.branch_id,
      };
      await updateUser(selectedUser.id, updateData);
      alert('User berhasil diupdate!');
      setShowEditForm(false);
      setSelectedUser(null);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Gagal update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (user: User) => {
    if (!confirm(`Yakin ingin deactivate user ${user.username}?`)) return;
    
    try {
      await deleteUser(user.id);
      alert(`User ${user.username} berhasil di-deactivate`);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Gagal deactivate user');
    }
  };

  const openEditForm = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '', // Not used in edit
      full_name: user.full_name,
      role: user.role,
      branch_id: user.branch_id,
    });
    setShowEditForm(true);
  };

  const openResetPassword = (user: User) => {
    setSelectedUser(user);
    setShowResetPassword(true);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'kasir': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const canEditUser = (user: User) => {
    if (currentUser?.role === 'owner') return true;
    if (currentUser?.role === 'admin') {
      return user.role === 'kasir' && user.branch_id === currentUser.branch_id;
    }
    return false;
  };

  const canResetPassword = (user: User) => {
    if (user.role === 'owner') return false;
    return canEditUser(user);
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-600">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">User Management</h1>
          <p className="text-gray-600">Kelola user dan hak akses</p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Filters */}
            <div className="flex gap-3">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Semua Role</option>
                <option value="owner">Owner</option>
                <option value="admin">Admin</option>
                <option value="kasir">Kasir</option>
              </select>

              {currentUser?.role === 'owner' && (
                <select
                  value={filterBranch || ''}
                  onChange={(e) => setFilterBranch(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Semua Cabang</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.code} - {branch.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Create Button */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 active:bg-blue-800"
            >
              + Tambah User
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Username</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nama</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Cabang</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{user.username}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{user.full_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.branch_id ? `Cabang #${user.branch_id}` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {canEditUser(user) && (
                          <>
                            <button
                              onClick={() => openEditForm(user)}
                              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                              Edit
                            </button>
                            {canResetPassword(user) && (
                              <button
                                onClick={() => openResetPassword(user)}
                                className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                              >
                                Reset PW
                              </button>
                            )}
                          </>
                        )}
                        {currentUser?.role === 'owner' && user.id !== currentUser.id && (
                          <button
                            onClick={() => handleDeactivateUser(user)}
                            disabled={!user.is_active}
                            className={`px-3 py-1 text-xs rounded ${
                              user.is_active 
                                ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Tidak ada user yang ditemukan
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">Tambah User Baru</h2>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Username *</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                      minLength={3}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Password *</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Nama Lengkap *</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {currentUser?.role === 'owner' && <option value="admin">Admin</option>}
                      <option value="kasir">Kasir</option>
                    </select>
                  </div>

                  {formData.role !== 'owner' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Cabang *</label>
                      <select
                        value={formData.branch_id || ''}
                        onChange={(e) => setFormData({ ...formData, branch_id: parseInt(e.target.value) })}
                        required
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Pilih Cabang</option>
                        {branches.map(branch => (
                          <option key={branch.id} value={branch.id}>
                            {branch.code} - {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      {loading ? 'Menyimpan...' : 'Simpan'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                    >
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditForm && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">Edit User: {selectedUser.username}</h2>
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nama Lengkap *</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {currentUser?.role === 'owner' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Role *</label>
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                          required
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="admin">Admin</option>
                          <option value="kasir">Kasir</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Cabang *</label>
                        <select
                          value={formData.branch_id || ''}
                          onChange={(e) => setFormData({ ...formData, branch_id: parseInt(e.target.value) })}
                          required
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Pilih Cabang</option>
                          {branches.map(branch => (
                            <option key={branch.id} value={branch.id}>
                              {branch.code} - {branch.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      {loading ? 'Menyimpan...' : 'Update'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditForm(false);
                        setSelectedUser(null);
                      }}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                    >
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPassword && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="max-w-md w-full">
              <ResetPasswordForm
                userId={selectedUser.id}
                username={selectedUser.username}
                onSuccess={() => {
                  setShowResetPassword(false);
                  setSelectedUser(null);
                }}
                onCancel={() => {
                  setShowResetPassword(false);
                  setSelectedUser(null);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;
